import { Button } from '@/components/common/button/Button';
import { Confetti } from '@/components/common/Confetti';
import { Modal } from '@/components/common/Modal';
import { SolidgatePaymentStatus } from '@/types/payment';
import { getChatActions } from '@feature/chat/chat';
import { getSolidgateError } from '@feature/payment/solidgate/config/solidgateErrors.config';
import { selectSolidgatePaymentStatus } from '@/store/payment/paymentSlice';
import { useAppSelector } from '@/hooks/redux';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSolidgatePaymentStatusModal } from '@feature/payment/solidgate/hooks/useSolidgateModals';
import { LottieAnimation } from '@/components/common/LottieAnimation';

// Styles
import cn from 'classnames';
import s from './SolidgatePaymentStatusModal.module.less';

/**
 * SolidgatePaymentSuccessModal is a modal component that displays a success message after a payment is completed.
 *
 * TODO: Rewrite this modal, and make it more generic,
 *       so it can be used for Error, Wait or Success messages.
 *
 * @see SolidgatePaymentFormModal - this modal is shown after the payment form is submitted successfully.
 */
export const SolidgatePaymentStatusModal: FC = () => {
  const navigate = useNavigate();

  const { id, code, idOrder } = useAppSelector(selectSolidgatePaymentStatus);
  const errorPayload = getSolidgateError(code);

  const currentModal = useSolidgatePaymentStatusModal();

  const navigateToOrder = () => {
    currentModal.close();
    // navigate to the order page if idOrder is provided
    idOrder && navigate(`/orders/${idOrder}`);
  };

  const { showChat } = getChatActions();

  const isProcessing = id === SolidgatePaymentStatus.Pending;

  /**
   * [case] If user tries to close the status modal (by refreshing the page),
   * we should prevent display it again after refresh,
   * because status modal state persists in local storage.
   */
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!id) return;
      // Show confirmation dialog
      event.preventDefault();
      event.stopPropagation();
      event.returnValue = 'Payment is in progress. Are you sure you want to leave?';
      // Optionally close modal or perform other actions
      currentModal.close();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [id, currentModal]);

  // Don't render the modal if no id status is provided
  if (!id) return null; // If no id is provided, do not render the modal

  /**
   * Important: The status name also used for the animation file name
   * and CSS class name.
   */
  const status = {
    [SolidgatePaymentStatus.Pending]: 'pending',
    [SolidgatePaymentStatus.Success]: 'success',
    [SolidgatePaymentStatus.Failed]: 'failed',
  }[id];

  const contactSupportLink = (
    <span>
      If you have any questions, please feel free to{' '}
      <span className={s.link} onClick={showChat}>
        contact our support team.
      </span>
    </span>
  );

  const content = {
    [SolidgatePaymentStatus.Pending]: {
      heading: <>Finalizing Your Payment</>,
      description: (
        <>
          We&apos;re handling your payment now. Please hang tight without closing or reloading the page. It should be completed within
          several minutes.
        </>
      ),
      buttons: null,
    },
    [SolidgatePaymentStatus.Success]: {
      heading: <>Payment Successful!</>,
      description: <>Thank you for your purchase! Your order is now with our team of experts. {contactSupportLink}</>,
      buttons: (
        <>
          <Button size="large" modification="success" onClick={navigateToOrder}>
            View Order
          </Button>
        </>
      ),
    },
    [SolidgatePaymentStatus.Failed]: {
      heading: (
        <>
          {code ? (
            <>
              Payment Failed!
              <small>
                {errorPayload.title} {code}
              </small>
            </>
          ) : (
            <>
              Unknown error!
              <small>{errorPayload.title}</small>
            </>
          )}
        </>
      ),
      description: (
        <>
          {errorPayload.description}
          {code && (
            <>
              <br />
              Close dialog and click &quot;Pay Now&quot; button again to retry.
            </>
          )}
        </>
      ),
      buttons: (
        <>
          <Button size="large" onClick={currentModal.close}>
            <strong>Try Again</strong>
          </Button>
          <Button size="large" onClick={showChat}>
            Contact Support
          </Button>
        </>
      ),
    },
  }[id];

  return (
    <>
      <Confetti initiated={id === SolidgatePaymentStatus.Success} />
      <Modal
        closable={!isProcessing}
        width={680}
        open={!!id}
        className={cn(s.wrapper, s[status])}
        onCancel={!isProcessing ? currentModal.close : undefined}
      >
        <div className={s.animation}>
          <LottieAnimation name={status} loop={isProcessing} delay={350} size={160} />
        </div>

        <div className={s.heading}>{content.heading}</div>
        <p className={s.description}>{content.description} </p>
        <div className={s.buttons}>{content.buttons}</div>
      </Modal>
    </>
  );
};
